import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { ActivityIndicator, Modal, View } from 'react-native';
import { useForm } from 'react-hook-form';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { useQuery } from '@tanstack/react-query';
import { WindowInformation } from '../../../services/window-information';
import MyAppText from '../../../components/MyAppText';
import FormInput from '../../../components/FormInput';
import { translate } from '../../../services/translate';
import FormActions from '../../../components/FormActions';
import Toast from 'react-native-toast-message';
import { useAuth } from '../../../context/Auth';
import { AlreadyHasDefaultTicketReason, crmService } from '../../../services/central-api/crm';
import FormSwitch from '../../../components/FormSwitch';
import CompanyDropdown from '../../../components/CompanyDropdown';
import DefinedColorPicker from '../../../components/DefinedColorPicker';
import { DEFAULT_PASTEL_COLORS, DEFAULT_PASTEL_TEXT_COLORS } from '../../../styles/colorPalettes';
import Badge from '../../../components/Badge';
import { ScrollView } from 'react-native-web-hover';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    selectedTicketReasonId: number | undefined;
    onClose: () => void;
}

export default function TicketReasonModal({ isModalVisible, onClose, selectedTicketReasonId, setModalVisible }: ModalProps) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const { user: authenticatedUser } = useAuth();

    const queryResult = useQuery({
        queryKey: ['ticketReasonModal', selectedTicketReasonId],
        queryFn: () => crmService.getTicketReason(selectedTicketReasonId!),
        enabled: !!selectedTicketReasonId && !!authenticatedUser,
    });

    const isEnabled = !!selectedTicketReasonId && !!authenticatedUser;
    const isFetchingInformation = isEnabled && queryResult.isLoading;

    const { data, refetch } = queryResult;

    const defaultEmptyValues: Omit<TicketReason, 'id' | 'createdAt' | 'updatedAt'> & { textColor: string; } = {
        name: '',
        isDefault: false,
        duration: 0,
        observation: '',
        companyId: 0,
        color: DEFAULT_PASTEL_COLORS[0],
        textColor: DEFAULT_PASTEL_TEXT_COLORS[0],
        requiresAttachment: false
    };

    const form = useForm<Omit<TicketReason, 'id' | 'createdAt' | 'updatedAt'> & { textColor: string; }>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    const closeModal = () => {
        form.reset(defaultEmptyValues);
        setModalVisible(false);
        onClose();
    };

    async function formSubmit() {
        try {
            const data = form.getValues();

            if (selectedTicketReasonId) {
                await crmService.editTicketReason({
                    ...data,
                    id: selectedTicketReasonId,
                });
            } else {
                await crmService.createTicketReason({
                    ...data,
                    duration: Number(data.duration)
                });
            }
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            closeModal();
        } catch (err) {
            if (err instanceof AlreadyHasDefaultTicketReason) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                    visibilityTime: 10000,
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    useEffect(() => {
        if (!authenticatedUser || !data) {
            return;
        }
        form.reset({
            ...data,
            textColor: DEFAULT_PASTEL_TEXT_COLORS[DEFAULT_PASTEL_COLORS.indexOf(data.color)]
        });

    }, [selectedTicketReasonId, data, authenticatedUser]);


    useEffect(() => {
        if (!isModalVisible || !authenticatedUser) {
            return;
        }

        if (selectedTicketReasonId) {
            refetch();
        } else {
            form.reset(defaultEmptyValues);
        }
    }, [selectedTicketReasonId, authenticatedUser, isModalVisible]);

    return (
        <Modal
            transparent
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => closeModal()}
        >
            <View style={styles.container}>
                <View style={styles.formContainer(windowInfo.isMobile)}>
                    {isFetchingInformation
                        ? <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', minHeight: 250 }}>
                            <ActivityIndicator size='large' color={theme.colors.loadingColor} />
                        </View>
                        : <>
                            <View style={{ flexDirection: 'column', rowGap: 8 }}>
                                <MyAppText style={styles.header}>{translate('drpScenarioRegister')}</MyAppText>
                                <MyAppText style={styles.subheader}>{translate('fillFieldsBelowToFinishDrpScenarioRegister')}</MyAppText>
                            </View>
                            <ScrollView style={{ flex: 1 }} contentContainerStyle={{ flexDirection: 'column', rowGap: 24 }} >
                                <CompanyDropdown
                                    form={form}
                                    fieldName='companyId'
                                    required={true}
                                    disabled={!!selectedTicketReasonId}
                                    labelStyle={styles.inputLabelStyle}
                                    zIndex={2}
                                />
                                <FormInput
                                    label={translate('name')}
                                    required={true}
                                    form={form}
                                    disabled={!!selectedTicketReasonId}
                                    fieldName='name'
                                    labelStyle={styles.inputLabelStyle}
                                />
                                <View style={{ flexDirection: 'row' }}>
                                    <FormSwitch
                                        label={translate('defaultReason')}
                                        form={form}
                                        viewStyle={{ flex: 1 }}
                                        fieldName='isDefault'
                                        labelStyle={styles.inputLabelStyle}
                                        hintText={<View style={{ flexDirection: 'column', zIndex: 9999 }}>
                                            <MyAppText style={{ color: theme.colors.white['500'] }}>{translate('ticketReasonDefaultHint')}</MyAppText>
                                        </View>}
                                        hintTextConfig={{
                                            maxWidth: 200
                                        }}
                                    />
                                    <FormSwitch
                                        label={translate('requiresAttachment')}
                                        form={form}
                                        viewStyle={{ flex: 1 }}
                                        fieldName='requiresAttachment'
                                        labelStyle={styles.inputLabelStyle}
                                        hintText={<View style={{ flexDirection: 'column', zIndex: 9999 }}>
                                            <MyAppText style={{ color: theme.colors.white['500'] }}>{translate('ticketReasonAttachmentHint')}</MyAppText>
                                        </View>}
                                        hintTextConfig={{
                                            tooltipDirection: 'left',
                                            maxWidth: 200
                                        }}
                                    />
                                </View>
                                <DefinedColorPicker
                                    colors={DEFAULT_PASTEL_COLORS}
                                    textColors={DEFAULT_PASTEL_TEXT_COLORS}
                                    labelStyle={styles.inputLabelStyle}
                                    label={translate('tagColor')}
                                    selectedColor={form.watch('color')}
                                    onColorSelect={(value) => form.setValue('color', value)}
                                    onColorSelectText={(value) => form.setValue('textColor', value)}
                                    numColumns={windowInfo.isMobile ? 4 : undefined}
                                />
                                <View style={styles.previewContainer}>
                                    <MyAppText style={styles.previewText}>{translate('preview')}</MyAppText>
                                    {form.formState.isValid
                                        ? <Badge mode='ROUNDED' backgroundColor={form.watch('color')} textColor={form.watch('textColor')} >{form.watch('name')}</Badge>
                                        : <MyAppText style={styles.previewNoContent} >{translate('fillPreviousFields')}</MyAppText>}
                                </View>
                                <FormInput
                                    label={translate('durationInMinutes')}
                                    required={true}
                                    form={form}
                                    fieldName='duration'
                                    labelStyle={styles.inputLabelStyle}
                                    mode='NUMBER'
                                    minValue={1}
                                    disabled={!!selectedTicketReasonId}
                                />
                                <FormInput
                                    label={translate('sendMessage')}
                                    form={form}
                                    fieldName='observation'
                                    multiline
                                    numberOfLines={3}
                                    maxLength={65535}
                                    labelStyle={styles.inputLabelStyle}
                                />
                            </ScrollView>
                            <FormActions
                                viewStyle={{ justifyContent: 'flex-end' }}
                                buttonStyle={{ paddingHorizontal: 16, paddingVertical: 9 }}
                                buttonContentContainerStyle={{ minWidth: 100 }}
                                onSubmit={formSubmit}
                                onClose={closeModal}
                                disabled={!form.formState.isValid}
                                isLoading={isFetchingInformation}
                            />
                        </>
                    }
                </View>
            </View>
        </Modal >
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
        paddingHorizontal: 16,
    },
    formContainer: (isMobile: boolean) => ({
        width: isMobile ? '95%' : 635,
        height: isMobile ? '90%' : 'auto',
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 24,
        rowGap: 32,
    }),
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 14,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
    inputLabelStyle: {
        fontSize: 14,
        fontWeight: '600',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
        marginBottom: 8,
    },
    previewContainer: {
        flexDirection: 'column',
        rowGap: 8,
    },
    previewText: {
        fontSize: 14,
        fontWeight: '600',
        color: theme.colors.primary['500']
    },
    previewNoContent: {
        fontSize: 16,
        fontWeight: '400',
        color: theme.colors.neutral['400']
    }
}));
